import { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Switch,
  useConfirmDialog,
} from '@/components/ui';
import { api } from '@/lib/api';

/**
 * 🔴 O QUE ESTE GRUPO RECEBE — a tela que decide, num lugar só (13/09/2026).
 *
 * ── De onde ela veio
 *
 * A rodada nasceu do Matheus **confuso** diante de três opções de um menu,
 * procurando onde dizer que um grupo recebe só monitoramento. A resposta
 * existia desde a migration 539 — atrás de uma porta que só abria depois de ele
 * escolher justamente a opção que não queria. ⚠️ **Se esta tela precisar de
 * alguém explicando, ela falhou** — por melhor que seja o código.
 *
 * ── O que mudou em relação à tela antiga
 *
 * 1. 🔴 **Abre em QUALQUER grupo** — sem exigir papel, sem exigir vínculo.
 * 2. 🔴 **A frase do topo** responde *"por que não chegou?"* sem ler o resto.
 * 3. 🔴 **Salva no botão**, não a cada clique: antes existia um intervalo em
 *    que o grupo recebia o que já tinha sido desligado na tela.
 * 4. **Agrupada por assunto** e com **perfis** como atalho.
 * 5. ⚠️ **Diz o que a tela não controla** — comunicado não passa por aqui.
 */

type Assunto = 'monitoramento' | 'ordem_servico' | 'alarme_interno' | 'conversa';

interface AvisoNoPainel {
  chave: string;
  nome: string;
  assunto: Assunto;
  recebe: boolean;
  desligadoNoSistema: boolean;
  soInterno: boolean;
  receberiaAgora: boolean;
  motivo: string | null;
  explicacao: string | null;
}

interface Painel {
  grupoId: string;
  nome: string;
  dequem: 'cliente' | 'interno' | 'tipo' | 'ninguem';
  dequemDetalhe: string;
  interno: boolean;
  /** O roteamento chega neste grupo? Num grupo da IGM, só com papel Supervisão. */
  alcancado: boolean;
  /** #294: grupo de condomínio sem nenhuma escolha gravada — nasce calado. */
  nasceuCalado?: boolean;
  botDentro: boolean;
  botVistoEm: string | null;
  avisos: AvisoNoPainel[];
  aceita: number;
  total: number;
  perfil: { chave: string; nome: string; alteracoes: number } | null;
  frase: string;
  impedimentoGeral: string | null;
  perfis: Array<{ chave: string; nome: string; assuntos: Assunto[] | null }>;
  assuntos: Record<Assunto, string>;
}

/** A ordem em que os assuntos aparecem — a mesma do catálogo. */
const ORDEM_DOS_ASSUNTOS: Assunto[] = ['monitoramento', 'ordem_servico', 'alarme_interno', 'conversa'];

/** 🔴 Escritos para leigo: nada de "so_interno", "opt-out", "flag" ou "porta". */
const AJUDA_DO_ASSUNTO: Record<Assunto, string> = {
  monitoramento:
    'Os avisos que nascem dos coletores instalados nos clientes: câmera, gravador ou rede que caiu, que está oscilando, que ficou lenta ou que está perdendo dados. O sistema espera um tempo antes de avisar, para não gritar por uma piscada — e o mesmo equipamento do mesmo cliente não é repetido antes de 24 horas.',
  ordem_servico:
    'Os avisos que nascem do Auvo, o sistema que os técnicos usam em campo: o técnico saiu para o atendimento, o atendimento terminou, o chamado chegou, o chamado foi encerrado.',
  alarme_interno:
    'Avisos sobre o próprio sistema, para a equipe — não para o condomínio. O coletor de um cliente parou de mandar dados; o Auvo parou de avisar as ordens de serviço; a IA parou de reescrever as descrições; o bot foi retirado de algum grupo. Estes normalmente não vão para grupo de condomínio: para irem, aquele cliente precisa ser liberado na ficha dele, um a um.',
  conversa:
    'O único aviso que responde a alguém: quando uma pessoa escreve no grupo fora do horário de atendimento, o sistema responde dizendo como acionar o plantão. Ele só funciona se alguém escrever — os outros doze o sistema manda sozinho.',
};

export function AvisosDoGrupo({
  grupoId,
  nomeDoGrupo,
  aberto,
  onFechar,
  onSalvou,
}: {
  grupoId: string | null;
  nomeDoGrupo: string | null;
  aberto: boolean;
  onFechar: () => void;
  /** A lista de fora se atualiza — o selo do grupo muda com o que foi salvo. */
  onSalvou?: () => void;
}) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [escolhas, setEscolhas] = useState<Record<string, boolean>>({});
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [perfilAplicado, setPerfilAplicado] = useState<string | null>(null);
  const confirmar = useConfirmDialog();

  const carregar = useCallback(() => {
    if (!grupoId) return;
    setCarregando(true);
    setErro(null);
    api
      .get<Painel>(`/api/v1/admin/whatsapp/grupos/${encodeURIComponent(grupoId)}/painel`)
      .then((p) => {
        setPainel(p);
        setEscolhas(Object.fromEntries(p.avisos.map((a) => [a.chave, a.recebe])));
        setPerfilAplicado(null);
      })
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [grupoId]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  if (!painel && !carregando && !erro) {
    return (
      <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()} size="md">
        <DialogHeader>
          <DialogTitle>O que {nomeDoGrupo ?? 'este grupo'} recebe</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
        </DialogContent>
      </Dialog>
    );
  }

  /** 🔴 Nada é salvo até o botão — o mexido fica só aqui até lá. */
  const mexeu = painel ? painel.avisos.some((a) => escolhas[a.chave] !== a.recebe) : false;
  /** Grupo da IGM com papel Silencioso: o roteamento não chega, e nenhum interruptor vale. */
  const anuladoPeloPapel = Boolean(painel?.interno && !painel?.alcancado);
  const ligadosAgora = painel ? painel.avisos.filter((a) => escolhas[a.chave]).length : 0;

  function aplicarPerfil(chave: string) {
    if (!painel) return;
    const perfil = painel.perfis.find((p) => p.chave === chave);
    if (!perfil) return;
    setEscolhas(
      Object.fromEntries(
        painel.avisos.map((a) => [a.chave, perfil.assuntos === null ? true : perfil.assuntos.includes(a.assunto)]),
      ),
    );
    setPerfilAplicado(chave);
  }

  async function salvar() {
    if (!grupoId || !painel) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.put<{ painel: Painel }>(
        `/api/v1/admin/whatsapp/grupos/${encodeURIComponent(grupoId)}/avisos`,
        { avisos: painel.avisos.map((a) => ({ gatilho: a.chave, recebe: Boolean(escolhas[a.chave]) })) },
      );
      setPainel({ ...r.painel, perfis: painel.perfis, assuntos: painel.assuntos });
      setEscolhas(Object.fromEntries(r.painel.avisos.map((a) => [a.chave, a.recebe])));
      setPerfilAplicado(null);
      onSalvou?.();
      onFechar();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    } finally {
      setSalvando(false);
    }
  }

  /** ⚠️ Fechar sem salvar descarta — e avisa antes. */
  async function tentarFechar() {
    if (!mexeu) return onFechar();
    const ok = await confirmar({
      title: 'Sair sem salvar?',
      message: 'Você mexeu nos avisos deste grupo e ainda não salvou. Sair agora descarta o que você mudou.',
      confirmLabel: 'Sair e descartar',
      variant: 'destructive',
    });
    if (ok) onFechar();
  }

  /**
   * ⚠️ As duas armadilhas que o perfil precisa avisar NA HORA de aplicar, não
   * depois: elas foram medidas na rodada de 13/09 e são a diferença entre
   * "liguei quatro" e "chega um".
   */
  function avisoDoPerfil(): string | null {
    if (!painel || !perfilAplicado) return null;
    const ligados = painel.avisos.filter((a) => escolhas[a.chave]);
    const soInternosEmGrupoDeCliente =
      painel.interno || painel.dequem !== 'cliente' ? 0 : ligados.filter((a) => a.soInterno).length;
    const desligadosNoSistema = ligados.filter((a) => a.desligadoNoSistema).length;
    const partes: string[] = [];
    if (soInternosEmGrupoDeCliente > 0) {
      partes.push(
        `${soInternosEmGrupoDeCliente} ${soInternosEmGrupoDeCliente === 1 ? 'é aviso interno' : 'são avisos internos'} da IGM: ` +
          `${soInternosEmGrupoDeCliente === 1 ? 'só chega' : 'só chegam'} a um grupo de condomínio depois de o cliente ser liberado na ficha dele`,
      );
    }
    if (desligadosNoSistema > 0) {
      partes.push(
        `${desligadosNoSistema} ${desligadosNoSistema === 1 ? 'está desligado' : 'estão desligados'} no sistema inteiro, ` +
          `em "Os avisos" — ligar aqui não faz efeito enquanto ${desligadosNoSistema === 1 ? 'ele estiver' : 'estiverem'} desligado${desligadosNoSistema === 1 ? '' : 's'} lá`,
      );
    }
    if (partes.length === 0) return null;
    return `Dos ${ligados.length} que este perfil liga, ${partes.join('; e ')}.`;
  }

  const avisoPerfil = avisoDoPerfil();

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && void tentarFechar()} size="lg">
      <DialogHeader>
        <DialogTitle>O que {painel?.nome ?? nomeDoGrupo ?? 'este grupo'} recebe</DialogTitle>
      </DialogHeader>

      <DialogContent className="space-y-4">
        {erro && (
          <Alert variant="danger" data-testid="avisos-grupo-erro">
            {erro}
          </Alert>
        )}
        {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>}

        {painel && (
          <>
            {/* 🔴 A FRASE — a que responde "por que não chegou?" sozinha. */}
            <Alert
              variant={painel.aceita > 0 && painel.botDentro && !painel.impedimentoGeral ? 'info' : 'warning'}
              data-testid="painel-frase"
            >
              {painel.frase}
            </Alert>

            {/* 🔴 #294 — nasce calado, e o gesto é LIGAR. Dito antes dos interruptores. */}
            {painel.nasceuCalado && (
              <Alert variant="info" data-testid="painel-nasceu-calado">
                Este grupo nasceu calado: nada sai para ele até você ligar os avisos aqui embaixo e salvar. É de
                propósito — ligar é o gesto, e ninguém recebe queda de equipamento sem alguém ter escolhido.
              </Alert>
            )}

            {/* De quem é o grupo — sem isto, "por que este aviso não vale?" fica sem resposta. */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2">
              <Badge variant={painel.interno ? 'neutral' : 'info'} size="sm">
                {painel.interno ? 'Grupo da IGM' : painel.dequem === 'tipo' ? 'Acompanha um tipo de tarefa' : 'Grupo de condomínio'}
              </Badge>
              <span data-testid="painel-dequem">{painel.dequemDetalhe}</span>
              <HelpTip texto="Um grupo é de um condomínio ou é da IGM — nunca os dois. Grupo de condomínio recebe os avisos daquele condomínio. Grupo da IGM recebe os avisos de todos os clientes, com o nome do cliente na frente de cada mensagem, e serve para a equipe acompanhar." />
            </div>

            {/* Os perfis — atalho, nunca trava. */}
            <div className="rounded-lg border border-line p-3 dark:border-line-dark" data-testid="painel-perfis">
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Comece por um perfil pronto
                </span>
                <HelpTip texto="Um atalho: escolha um perfil e os interruptores certos já vêm ligados. Depois você muda o que quiser — o perfil não tranca nada. Se você mexer num interruptor, a tela passa a dizer 'com 1 alteração', só para lembrar que saiu do padrão." />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {painel.perfis.map((p) => (
                  <Button
                    key={p.chave}
                    size="sm"
                    variant={perfilAplicado === p.chave ? 'secondary' : 'ghost'}
                    onClick={() => aplicarPerfil(p.chave)}
                    data-testid={`painel-perfil-${p.chave}`}
                  >
                    {p.nome}
                  </Button>
                ))}
              </div>
              {painel.perfil && !mexeu && (
                <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3" data-testid="painel-perfil-atual">
                  Perfil atual: {painel.perfil.nome}
                  {painel.perfil.alteracoes > 0
                    ? `, com ${painel.perfil.alteracoes} ${painel.perfil.alteracoes === 1 ? 'alteração' : 'alterações'}`
                    : ''}
                </p>
              )}
              {avisoPerfil && (
                <p className="mt-2 text-xs text-warning" data-testid="painel-aviso-perfil">
                  ⚠ {avisoPerfil}
                </p>
              )}
            </div>

            {/* 🔴 O PAPEL VEM ANTES DOS INTERRUPTORES (FASE B, 13/09 à noite).
                Matheus, no Coletor IP: "não está recebendo... conforme
                configurado nos toggles, mas notei que o papel está em
                silencioso... está confuso". Treze interruptores aceitando
                configuração que o papel anula, sem dizer, é pior que um nome
                ruim. O aviso fica AQUI, onde os interruptores estão — e eles
                ficam apagados, com o motivo, enquanto o papel for Silencioso. */}
            {anuladoPeloPapel && (
              <Alert variant="warning" data-testid="painel-anulado-papel">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span>
                    Este grupo está como <strong>Silencioso</strong> e não recebe nada — mesmo com os
                    avisos abaixo ligados. Para ele acompanhar a operação, mude o papel para{' '}
                    <strong>Supervisão</strong> na lista de grupos: o que estiver ligado aqui passa a
                    valer na hora, para todos os clientes.
                  </span>
                  <HelpTip texto="O papel decide antes de qualquer interruptor. Silencioso não recebe nada. Supervisão faz o grupo entrar nos avisos de todos os clientes, com o nome do cliente na frente — e aí os interruptores desta tela escolhem quais tipos chegam. Supervisão não é 'recebe tudo': é 'recebe, de todos os clientes, o que estiver ligado aqui'." />
                </span>
              </Alert>
            )}

            {/* Os interruptores, por assunto. */}
            {ORDEM_DOS_ASSUNTOS.map((assunto) => {
              const doAssunto = painel.avisos.filter((a) => a.assunto === assunto);
              if (doAssunto.length === 0) return null;
              return (
                <div key={assunto} data-testid={`painel-assunto-${assunto}`}>
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                      {painel.assuntos[assunto]}
                    </h4>
                    <HelpTip texto={AJUDA_DO_ASSUNTO[assunto]} />
                  </div>
                  <div className="space-y-1.5">
                    {doAssunto.map((a) => (
                      <div
                        key={a.chave}
                        className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1${anuladoPeloPapel ? ' opacity-50' : ''}`}
                        data-testid={`avisos-grupo-linha-${a.chave}`}
                      >
                        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                          <span className="text-sm text-ink-1 dark:text-ink-dark-1">{a.nome}</span>
                          {a.desligadoNoSistema && (
                            <>
                              <Badge variant="warning" size="sm">
                                desligado no sistema
                              </Badge>
                              <HelpTip texto="Este aviso está desligado para todos os grupos, lá em 'Os avisos'. Ligar aqui não faz efeito enquanto ele estiver desligado lá." />
                            </>
                          )}
                          {anuladoPeloPapel && (
                            <Badge variant="neutral" size="sm">
                              anulado pelo papel
                            </Badge>
                          )}
                          {!anuladoPeloPapel && !a.receberiaAgora && escolhas[a.chave] && a.explicacao && !a.desligadoNoSistema && (
                            <HelpTip texto={a.explicacao} />
                          )}
                        </span>
                        <Switch
                          checked={Boolean(escolhas[a.chave])}
                          disabled={anuladoPeloPapel}
                          onChange={(e) => {
                            setEscolhas((s) => ({ ...s, [a.chave]: e.target.checked }));
                            setPerfilAplicado(null);
                          }}
                          label={escolhas[a.chave] ? 'Recebe' : 'Não recebe'}
                          data-testid={`avisos-grupo-${a.chave}`}
                          /* 🔴 A régua do celular: o track do Switch canônico tem
                             24px, e são TREZE deles num modal. O alvo cresce aqui
                             (o canônico já é um <label>, então cresce junto) sem
                             mexer no componente que a aplicação inteira usa. */
                          className="min-h-[44px] sm:min-h-0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* ⚠️ O que esta tela NÃO controla — dito, para ninguém supor. */}
            <p className="border-t border-line pt-3 text-xs text-ink-3 dark:text-ink-dark-3 dark:border-line-dark" data-testid="painel-comunicados">
              <strong>Comunicados não passam por aqui.</strong> Aquele aviso que você escreve e manda
              para vários condomínios de uma vez escolhe os grupos na hora do envio — desligar avisos
              nesta tela não impede este grupo de receber um comunicado.
            </p>
          </>
        )}
      </DialogContent>

      <DialogFooter>
        <span className="mr-auto text-xs text-ink-3 dark:text-ink-dark-3" data-testid="painel-contagem">
          {painel ? `${ligadosAgora} de ${painel.total} ligados` : ''}
          {mexeu ? ' · não salvo' : ''}
        </span>
        <Button variant="ghost" onClick={() => void tentarFechar()} disabled={salvando}>
          Cancelar
        </Button>
        <Button onClick={() => void salvar()} disabled={!mexeu || salvando} data-testid="painel-salvar">
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
